import { Box, useScaleFactor, useSpaceSize } from "arkh";
import { PixelRatio, Platform, TextInput } from "react-native";
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useSaveNoteValue } from "./useSaveNoteValue";
import cuid from "cuid";
import { graphql, useFragment } from "react-relay";
import { WidgetNoteTextModePage_widgetNotePage$key } from "./__generated__/WidgetNoteTextModePage_widgetNotePage.graphql";
import { useNoteWidgetPageTextStyle } from "widgets/src/WidgetNote/styleHelpers/useNoteWidgetStyle";
import { WidgetNotePageBackground } from "widgets/src/WidgetNote/WidgetNotePageBackground";
import { useWidgetNoteTextTopPadding } from "widgets/src/WidgetNote/useWidgetNoteTextTopPadding";
import { useDebouncedCallback } from "use-debounce";

export const WidgetNoteTextMode = (props: {
  widgetId: string;
  page: WidgetNoteTextModePage_widgetNotePage$key;
  ref: RefObject<TextInput | null>;
}) => {
  const page = useFragment(
    graphql`
      fragment WidgetNoteTextModePage_widgetNotePage on WidgetNotePage {
        currentValue

        updatedByActorId
        id
        ...WidgetNotePageBackground_widgetNotePage
        ...useNoteWidgetStyleText_widgetNotePage
      }
    `,
    props.page
  );
  const [_onSetNoteValue] = useSaveNoteValue(props.widgetId);
  const actorId = useRef(cuid());
  const actorIdSequence = useRef(0);

  const onSaveNoteValue = useCallback(
    (value: string, sequence: number) => {
      _onSetNoteValue(page.id, actorId.current, sequence, value);
    },
    [_onSetNoteValue, page.id]
  );

  const onSaveNoteValueDebounced = useDebouncedCallback(onSaveNoteValue, 600, {
    maxWait: 2000,
  });

  useEffect(() => {
    return () => {
      onSaveNoteValueDebounced.flush();
    };
  }, [onSaveNoteValueDebounced]);

  const [value, onSetValue] = useState(page.currentValue);

  // update the local value when the remote value changes
  // only if remote actorId is different from the local actorId
  useEffect(() => {
    if (page.updatedByActorId !== actorId.current) {
      onSetValue(page.currentValue);
    }
  }, [page.updatedByActorId, actorId, page.currentValue]);

  const onChangeText = useCallback(
    (text: string) => {
      onSetValue(text);
      onSaveNoteValueDebounced(text, ++actorIdSequence.current);
    },
    [onSetValue, onSaveNoteValueDebounced]
  );

  const sf = useScaleFactor();
  const padding = useSpaceSize("small");
  const paddingTop = useWidgetNoteTextTopPadding();
  const textStyle = useNoteWidgetPageTextStyle(page);

  const styleKey = useMemo(
    () => (Platform.OS === "ios" ? JSON.stringify(textStyle) : undefined),
    [textStyle]
  );

  // style key is used to recreate the input when the style changes
  // this is necessary due to a bug in TextInput on iOS https://github.com/facebook/react-native/issues/25874

  const textInputStyle = useMemo(
    () => ({
      padding,
      paddingTop,
      fontSize: PixelRatio.roundToNearestPixel(textStyle.fontSize * sf),
      fontStyle: textStyle.fontStyle,
      fontFamily: textStyle.fontFamily,
      textAlign: textStyle.textAlign,
      color: textStyle.color,
      fontWeight: "normal" as const,
      textAlignVertical: "top" as const, // android
      flex: 1,
    }),
    [
      padding,
      paddingTop,
      sf,
      textStyle.color,
      textStyle.fontFamily,
      textStyle.fontSize,
      textStyle.fontStyle,
      textStyle.textAlign,
    ]
  );

  return (
    <Box
      style={{
        flex: 1,
        height: "100%",
        width: "100%",
        alignItems: "flex-start",
        justifyContent: "center",
        overflow: "hidden",
        flexDirection: "row",
      }}
      cornerRadius="medium"
      color="darkText"
    >
      <WidgetNotePageBackground page={page} />
      <TextInput
        ref={props.ref}
        autoFocus
        key={styleKey}
        allowFontScaling={false}
        multiline
        scrollEnabled={false}
        style={[
          textInputStyle,
          {
            color: textStyle.color,
            flex: 1,
            height: "100%",
            zIndex: 9,
            overflow: "hidden",
          },
          textStyle.textShadow,
        ]}
        value={value}
        onChangeText={onChangeText}
      />
    </Box>
  );
};
